import React, { useMemo, useRef, useState } from "react";

import { Box, Checkbox, Grid, IconButton, Modal, Typography } from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import Button from "@remar/shared/dist/components/Button";
import { TextField } from "@remar/shared/dist/components/TextField";
import { useSimpleModalStyles } from "@remar/shared/dist/modals/SimpleModal/styles";

import { validZipCodeCharacter, validatePhoneNumber } from "@remar/shared/dist/utils/serviceUtils/validators";
import { Field, Form, Formik } from "formik";
import { useAppDispatch, useAppSelector } from "store";

import { getUserData, selectUser } from "store/features/Auth/authSlice";
import { getFullState as billingFullSate, updateBillingAddress } from "store/features/Billing/BillingSlice";

import {
	BuyButtons,
	PaymentAgreementRow,
	PaymentCardColumn,
	PaymentContent,
	PaymentOrderHeader,
	PaymentOrderSummary
} from "./styles";

import {
	PaymentContainer,
	PaymentStatusDescription,
	PaymentStatusErrorIcon,
	PaymentStatusHeader,
	PaymentStatusSuccessIcon
} from "../../QuestionBank/styles";

import { CountrySelect } from "../ChangeBillingAddress";
import PaymentMethods from "../PaymentMethods";
import { BillingAddressSchema } from "../schemas";

enum Steps {
	Shipping = "Shipping",
	Payment = "Payment"
}

type OneTimePaymentProps = {
	open: boolean;
	handleClose: () => void;
	handlePayment: () => void;
	isLoading: boolean;
	succeeded: boolean;
	successBody: React.ReactNode | null;
	failed: boolean;
	onTryAgain: () => void;
	orderSummary: React.ReactNode | null;
	successNote?: React.ReactNode | null;
	width?: string | number;
	height?: string | number;
	needAddress?: boolean;
};
const OneTimePayment = ({
	open,
	width,
	height = "auto",
	handleClose,
	handlePayment,
	isLoading,
	succeeded = false,
	successBody,
	successNote,
	failed = false,
	onTryAgain,
	orderSummary,
	needAddress = false
}: OneTimePaymentProps) => {
	const dispatch = useAppDispatch();
	const ref = useRef<HTMLDivElement>(null);
	const simpleModalClasses = useSimpleModalStyles();

	const { paymentMethods } = useAppSelector(billingFullSate);
	const user = useAppSelector(selectUser);

	const [step, setStep] = useState<Steps>(needAddress && !user?.userShippingDetails ? Steps.Shipping : Steps.Payment);
	const [isAgreed, setIsAgreed] = useState(false);

	const initialFormValues = useMemo(
		() => ({
			fullName: user!.userShippingDetails?.fullName || user!.fullName || `${user!.firstName} ${user!.lastName}` || "",
			countryId: user!.userShippingDetails?.countryId || "",
			address1: user!.userShippingDetails?.address1 || "",
			address2: user!.userShippingDetails?.address2 || "",
			city: user!.userShippingDetails?.city || "",
			state: user!.userShippingDetails?.state || "",
			zipCode: user!.userShippingDetails?.zipCode || "",
			phoneNumber: user!.userShippingDetails?.phoneNumber || ""
		}),
		[user]
	);

	const submitBillingAddress = async values => {
		const payload = {
			data: {
				...values,
				userId: user?.id
			},
			filters: { id: user?.id }
		};
		await dispatch(
			updateBillingAddress({
				payload,
				sideEffect: () => {
					dispatch(getUserData);
				}
			})
		);
	};

	return (
		<Modal className={simpleModalClasses.modal} disableEnforceFocus disableAutoFocus open={open} onClose={handleClose}>
			<PaymentContent width={width} height={height}>
				<Box position={"absolute"} right={"10px"} top={"10px"}>
					<IconButton onClick={handleClose}>
						<CloseIcon color="disabled" />
					</IconButton>
				</Box>
				{succeeded ? (
					<>
						<PaymentCardColumn>
							<PaymentStatusSuccessIcon />
							<PaymentStatusHeader>Payment Successful</PaymentStatusHeader>
							<PaymentStatusDescription>Congratulations on your purchase!</PaymentStatusDescription>
							{successNote}
						</PaymentCardColumn>
						{successBody}
					</>
				) : (
					<PaymentContainer>
						<PaymentCardColumn>
							{failed ? (
								<>
									<PaymentStatusErrorIcon />
									<PaymentStatusHeader>Something Went Wrong</PaymentStatusHeader>
									<PaymentStatusDescription>
										We are not able to process your payment.
										<br />
										Please try again.
									</PaymentStatusDescription>
									<Button size="medium" onClick={onTryAgain}>
										Try again
									</Button>
								</>
							) : step === Steps.Shipping ? (
								<Formik
									initialValues={initialFormValues}
									validationSchema={BillingAddressSchema}
									onSubmit={async values => {
										await submitBillingAddress(values);
										setStep(Steps.Payment);
									}}
								>
									{({ isValid, dirty }) => (
										<Form>
											<Box m={3}>
												<Typography variant="h5">Shipping address</Typography>
												<Box mt={3}>
													<Grid container spacing={3}>
														<Grid item xs={12}>
															<Field inputRef={ref} hiddenLabel component={CountrySelect} name="countryId" fullWidth />
														</Grid>
														<Grid item xs={12}>
															<Field
																hiddenLabel
																component={TextField}
																name="fullName"
																placeholder="Full Name"
																fullWidth
															/>
														</Grid>
														<Grid item xs={12}>
															<Field
																hiddenLabel
																component={TextField}
																name="address1"
																placeholder="Address Line 1"
																fullWidth
															/>
														</Grid>
														<Grid item xs={12}>
															<Field hiddenLabel component={TextField} name="address2" placeholder="Apt #" fullWidth />
														</Grid>
														<Grid item xs={12} sm={5}>
															<Field hiddenLabel component={TextField} name="city" placeholder="City" fullWidth />
														</Grid>
														<Grid item xs={6} sm={3}>
															<Field hiddenLabel component={TextField} name="state" placeholder="State" fullWidth />
														</Grid>
														<Grid item xs={6} sm={4}>
															<Field
																hiddenLabel
																component={TextField}
																name="zipCode"
																inputProps={{ maxLength: 10 }}
																placeholder="Zip code"
																onKeyPress={(event: React.KeyboardEvent) => {
																	if (!(event.key.length === 1 && validZipCodeCharacter(event.key))) {
																		event.preventDefault();
																	}
																}}
																fullWidth
															/>
														</Grid>
														<Grid item xs={12}>
															<Field
																hiddenLabel
																component={TextField}
																name="phoneNumber"
																onKeyPress={(event: React.KeyboardEvent) => {
																	if (!validatePhoneNumber(event.key)) {
																		event.preventDefault();
																	}
																}}
																placeholder="Phone Number"
																fullWidth
															/>
														</Grid>
													</Grid>
												</Box>
											</Box>
											<BuyButtons>
												<Button
													disabled={!isValid || !dirty || isLoading}
													loading={isLoading}
													variant="filled"
													color="primary"
													size="medium"
													type="submit"
												>
													Continue
												</Button>
											</BuyButtons>
										</Form>
									)}
								</Formik>
							) : (
								<>
									<PaymentMethods />
									<PaymentAgreementRow>
										<Checkbox checked={isAgreed} onChange={e => setIsAgreed(e.target.checked)} /> I agree to the Terms &
										Conditions
									</PaymentAgreementRow>
									<BuyButtons>
										<Button
											variant={"filled"}
											color={"primary"}
											size="medium"
											disabled={!isAgreed || isLoading || !paymentMethods}
											onClick={handlePayment}
											loading={isLoading}
										>
											Complete Purchase
										</Button>
									</BuyButtons>
								</>
							)}
						</PaymentCardColumn>
						<PaymentOrderSummary>
							<PaymentOrderHeader>Order Summary</PaymentOrderHeader>
							{orderSummary}
						</PaymentOrderSummary>
					</PaymentContainer>
				)}
			</PaymentContent>
		</Modal>
	);
};

export default OneTimePayment;
